import styled from '@emotion/styled';
import { Text, tokens } from '@hotjar/hotsauce';
import type {
  CategoryName,
  FeedbackData,
  HeatmapsData,
  InsightsData,
  RecordingsData,
  SurveyData,
} from '@hotjar/webapp/domains/activation/featured-insights/types';
import {
  FeedbackSidebar,
  HeatmapSidebar,
  RecordingSidebar,
  SurveySidebar,
} from './SidebarInsights';

const Sidebar = styled.div`
  flex: 0.3;
  height: 100%;
  overflow: scroll;
  border-left: 1px solid ${tokens.color.ui_border};
`;

const CopyContainer = styled.div`
  padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x2};
`;

const Title = styled(Text)`
  display: block;
`;

const DetailsList = styled(Text)`
  display: block;
  margin-top: ${tokens.spacer.x1};
  color: ${tokens.color.text_light};
`;

export type FeaturedInsightSidebarProps = {
  data: InsightsData;
  category: CategoryName;
};

export const formatHeatmapPageUrl = (heatmapId: string): string => {
  try {
    const heatmapURL = heatmapId.startsWith('http') ? heatmapId : `https://${heatmapId}`;
    return new URL(heatmapURL).pathname;
  } catch (err) {
    return heatmapId;
  }
};

export const FeaturedInsightSidebar = ({
  category,
  data,
}: FeaturedInsightSidebarProps): JSX.Element => {
  const renderSidebar = () => {
    switch (category) {
      case 'recordings':
        return <RecordingSidebar data={data as RecordingsData} />;
      case 'heatmaps':
        return <HeatmapSidebar data={data as HeatmapsData} />;
      case 'feedback':
        return <FeedbackSidebar data={data as FeedbackData} />;
      case 'surveys':
        return <SurveySidebar data={data as SurveyData} />;

      default:
        return <RecordingSidebar data={data as RecordingsData} />;
    }
  };

  const renderTitle = () => {
    switch (category) {
      case 'heatmaps':
        return formatHeatmapPageUrl((data as HeatmapsData).id);
      case 'surveys':
        return (data as SurveyData).name;
      default:
        return data.id;
    }
  };

  return (
    <Sidebar>
      <CopyContainer>
        <Title textType="subtitle2">{renderTitle()}</Title>
        <DetailsList textType="caption">{renderSidebar()}</DetailsList>
      </CopyContainer>
    </Sidebar>
  );
};
